import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { getUserCollection } from './userIntegrationService';
import { getJobTreadCustomFields } from './jobtreadService';

/**
 * JobTread custom-field → portal-concept mapping.
 *
 * In JobTread, business fields like lead source and contract value are org-
 * defined CUSTOM FIELDS, not built-in job columns. This module auto-matches
 * the org's custom-field definitions to the portal concepts by name, persists
 * the result on the user's integration doc, and only surfaces anything to the
 * user when a match is ambiguous or missing. No manual setup required for
 * orgs with conventionally named fields.
 *
 * Stored at {users|internal_users}/{uid}.integrations.jobtread.fieldMappings:
 * {
 *   organizationId, version, resolvedAt, resolvedBy: 'auto'|'manual',
 *   fields: { leadSource: {fieldId, fieldName, confidence}|null, contractValue: ..., jobStatus: ..., probability: ... },
 *   unresolved: ['jobStatus'],
 *   ambiguous: { contractValue: [{fieldId, fieldName}, ...] },
 *   availableCustomFields: [{id, name, type, targetType}]   // snapshot for dev mode
 * }
 */

export const MAPPING_VERSION = 1;

/** Name heuristics per portal concept, tried in order; first list entry wins ties. */
export const PORTAL_FIELD_HEURISTICS = {
  leadSource: [
    /^lead.?source$/i,
    /lead.?source/i,
    /source.?of.?lead/i,
    /referral.?source/i,
    /how.?did.?you.?hear/i,
    /marketing.?source/i
  ],
  contractValue: [
    /^contract.?(value|amount|price)$/i,
    /contract.?(value|amount|price)/i,
    /^(project|job).?(value|amount)$/i,
    /(project|job).?(value|amount)/i,
    /(sale|sold).?(price|amount)/i,
    /estimate.?(value|amount)/i,
    /budget/i
  ],
  jobStatus: [
    /^stage$/i,
    /^(job.?)?status$/i,
    /pipeline.?stage/i,
    /sales.?stage/i
  ],
  probability: [
    /^probability$/i,
    /probability/i,
    /confidence/i,
    /likelihood/i,
    /chance.?(of|to).?(close|win)/i
  ],
  qualifiedLead: [
    /^qualified$/i,
    /qualified.?lead/i,
    /^is.?qualified$/i,
    /qualified/i
  ]
};

/** Custom-field targetTypes that can carry job-level data. */
const JOB_TARGET_TYPES = ['job'];

/**
 * Pure matcher: run the heuristics over an org's custom-field definitions.
 * A concept resolves only when exactly one field matches its highest-priority
 * matching pattern; multiple candidates are recorded as ambiguous.
 *
 * @param {Array<{id:string,name:string,type:string,targetType:string}>} customFields
 * @returns {{ fields: Object, unresolved: string[], ambiguous: Object }}
 */
export const matchCustomFields = (customFields) => {
  const jobFields = (customFields || []).filter(
    (f) => f?.id && f?.name && (!f.targetType || JOB_TARGET_TYPES.includes(String(f.targetType).toLowerCase()))
  );

  const fields = {};
  const unresolved = [];
  const ambiguous = {};

  Object.entries(PORTAL_FIELD_HEURISTICS).forEach(([concept, patterns]) => {
    let resolved = null;
    let candidates = [];
    for (const pattern of patterns) {
      candidates = jobFields.filter((f) => pattern.test(f.name));
      if (candidates.length === 1) {
        resolved = candidates[0];
        break;
      }
      if (candidates.length > 1) {
        // Ambiguous at this priority level — don't guess.
        break;
      }
    }
    if (resolved) {
      fields[concept] = { fieldId: resolved.id, fieldName: resolved.name, confidence: 'auto' };
    } else {
      fields[concept] = null;
      if (candidates.length > 1) {
        ambiguous[concept] = candidates.map((f) => ({ fieldId: f.id, fieldName: f.name }));
      } else {
        unresolved.push(concept);
      }
    }
  });

  return { fields, unresolved, ambiguous };
};

const mappingPath = 'integrations.jobtread.fieldMappings';

/**
 * Read the cached mapping; recompute from the org's custom fields and persist
 * when absent, for a different org, or forceRefresh. Never throws.
 *
 * `targetUserId` scopes the custom-fields query when resolving on behalf of
 * another user (CRM Portal View) — the server then uses that user's stored
 * grant; without it the query runs under the logged-in viewer's own grant.
 *
 * @returns {Promise<{ mapping: Object|null, error: string|null }>}
 */
export const resolveJobTreadFieldMappings = async (userId, organizationId, { forceRefresh = false, targetUserId = null } = {}) => {
  if (!userId || !organizationId) return { mapping: null, error: 'Missing user or organization id' };
  try {
    const collectionName = await getUserCollection(userId);
    const userRef = doc(db, collectionName, userId);
    const snap = await getDoc(userRef);
    const existing = snap.exists() ? snap.data()?.integrations?.jobtread?.fieldMappings : null;

    if (
      !forceRefresh &&
      existing &&
      existing.organizationId === organizationId &&
      existing.version === MAPPING_VERSION &&
      // An empty snapshot is treated as stale — a cache written while the
      // custom-fields query failed would otherwise never self-correct.
      (existing.availableCustomFields?.length ?? 0) > 0
    ) {
      return { mapping: existing, error: null };
    }

    // (Re)build from the org's custom-field definitions.
    const cfData = await getJobTreadCustomFields(organizationId, { targetUserId });
    const cfNodes = cfData?.organization?.customFields?.nodes ?? [];
    const customFields = (Array.isArray(cfNodes) ? cfNodes : [cfNodes]).filter(Boolean);

    const { fields, unresolved, ambiguous } = matchCustomFields(customFields);
    const mapping = {
      organizationId,
      version: MAPPING_VERSION,
      resolvedAt: new Date().toISOString(),
      resolvedBy: existing?.resolvedBy === 'manual' && existing.organizationId === organizationId ? 'manual' : 'auto',
      fields:
        existing?.resolvedBy === 'manual' && existing.organizationId === organizationId
          ? { ...fields, ...existing.fields } // manual choices win over heuristics
          : fields,
      unresolved,
      ambiguous,
      availableCustomFields: customFields.map((f) => ({
        id: f.id,
        name: f.name,
        type: f.type || null,
        targetType: f.targetType || null
      }))
    };

    try {
      if (snap.exists()) {
        await updateDoc(userRef, { [mappingPath]: mapping });
      } else {
        await setDoc(userRef, { integrations: { jobtread: { fieldMappings: mapping } } }, { merge: true });
      }
    } catch (persistError) {
      // Persisting is best-effort; the in-memory mapping still works this session.
      console.warn('resolveJobTreadFieldMappings: persist failed:', persistError);
    }

    return { mapping, error: null };
  } catch (error) {
    console.error('resolveJobTreadFieldMappings error:', error);
    return { mapping: null, error: error?.message || 'Failed to resolve JobTread field mappings' };
  }
};

/** Manual override (e.g., from a future settings UI). */
export const saveJobTreadFieldMappings = async (userId, mapping) => {
  const collectionName = await getUserCollection(userId);
  const userRef = doc(db, collectionName, userId);
  await updateDoc(userRef, {
    [mappingPath]: { ...mapping, resolvedBy: 'manual', resolvedAt: new Date().toISOString(), version: MAPPING_VERSION }
  });
};

/** Manually pin one concept (e.g. leadSource) to a specific custom field. */
export const saveJobTreadFieldChoice = async (userId, mapping, concept, field) => {
  await saveJobTreadFieldMappings(userId, {
    ...mapping,
    fields: {
      ...(mapping?.fields || {}),
      [concept]: field ? { fieldId: field.id, fieldName: field.name, confidence: 'manual' } : null
    },
    unresolved: (mapping?.unresolved || []).filter((c) => c !== concept)
  });
};

/*
 * Lead preferences — what the org calls a "lead" in JobTread.
 * Stored at {users|internal_users}/{uid}.integrations.jobtread.leadPreferences:
 * {
 *   leadEntity: 'job'|'customer',       // New Job vs New Customer
 *   leadScope: 'all'|'status',          // all created, or in specific status
 *   statusFieldId: string,              // '' = built-in status, else a custom field id
 *   statuses: string[],                 // selected values of the status field
 *   updatedAt
 * }
 */
const leadPrefsPath = 'integrations.jobtread.leadPreferences';

export const loadJobTreadLeadPreferences = async (userId) => {
  try {
    const collectionName = await getUserCollection(userId);
    const snap = await getDoc(doc(db, collectionName, userId));
    return snap.exists() ? snap.data()?.integrations?.jobtread?.leadPreferences || null : null;
  } catch (e) {
    console.error('loadJobTreadLeadPreferences error:', e);
    return null;
  }
};

/**
 * Normalize any stored/partial prefs to the current shape, mapping the earlier
 * leadDefinition model (jobs / jobStatuses / customers) forward.
 */
export const normalizeLeadPreferences = (prefs) => {
  if (prefs?.leadEntity) {
    return {
      leadEntity: prefs.leadEntity === 'customer' ? 'customer' : 'job',
      leadScope: prefs.leadScope === 'status' ? 'status' : 'all',
      statusFieldId: prefs.statusFieldId || '',
      statuses: Array.isArray(prefs.statuses) ? prefs.statuses : []
    };
  }
  // Legacy leadDefinition → new shape.
  const def = prefs?.leadDefinition;
  if (def === 'customers') return { leadEntity: 'customer', leadScope: 'all', statusFieldId: '', statuses: [] };
  if (def === 'jobStatuses') {
    return { leadEntity: 'job', leadScope: 'status', statusFieldId: '', statuses: Array.isArray(prefs.statuses) ? prefs.statuses : [] };
  }
  return { leadEntity: 'job', leadScope: 'all', statusFieldId: '', statuses: [] };
};

export const saveJobTreadLeadPreferences = async (userId, prefs) => {
  const collectionName = await getUserCollection(userId);
  const userRef = doc(db, collectionName, userId);
  const payload = {
    ...normalizeLeadPreferences(prefs),
    updatedAt: new Date().toISOString()
  };
  const snap = await getDoc(userRef);
  if (snap.exists()) {
    await updateDoc(userRef, { [leadPrefsPath]: payload });
  } else {
    await setDoc(userRef, { integrations: { jobtread: { leadPreferences: payload } } }, { merge: true });
  }
  return payload;
};
